import { call, put, select, takeLatest } from 'redux-saga/effects';
import { PERIOD_TYPE } from 'domain/period-analyze/consts';
import { getProductAnalysis, exportCsv } from 'services/api/AdebifyApi';
import { commonActions } from 'store/common';
import { loggerActions } from 'store/logger';
import loggerConstants from 'store/logger/constant';
import settingsSelector from 'store/settings/selectors';
import filterSelectors from 'store/filters/selectors';
import { mapAdebifyFilters } from 'domain/adebify/filterMapping';
import dedupeDimensions from 'domain/adebify/dedupeDimensions';
import types from './types';
import actions from './actions';
import { selectParams } from './selectors';

// Adebify backend (jug-api) accepts {daily, monthly, hourly, dow}. We keep
// the shared PERIOD_TYPE constants in redux to align with period-analyze and
// translate at the API boundary, the same pattern PERIOD_TYPE_TO_API_MAP uses
// for period-analyze (day_of_week vs dow is the only divergence).
const PERIOD_TYPE_TO_ADEBIFY_API = {
  [PERIOD_TYPE.DAY]: 'daily',
  [PERIOD_TYPE.MONTH]: 'monthly',
  [PERIOD_TYPE.HOUR]: 'hourly',
  [PERIOD_TYPE.DOW]: 'dow',
};

function* fetchProductAnalysisData() {
  const params = yield select(selectParams);
  const period = yield select(
    (state) => state.commonState.CommonState.settings.period
  );

  if (!period?.start || !period?.end) {
    return;
  }

  const startDate = period.start.format('YYYY-MM-DD');
  const endDate = period.end.format('YYYY-MM-DD');

  const apiParams = {
    start_date: startDate,
    end_date: endDate,
    product_axis: params.product_axis,
    axis: params.axis,
    dimensions: dedupeDimensions(params.dimensions),
    period_type:
      PERIOD_TYPE_TO_ADEBIFY_API[params.period_type] || params.period_type,
    graph_metric: params.graph_metric,
    sort_order: params.sort_order,
    limit: params.limit,
    offset: params.offset,
  };

  if (params.metrics) {
    apiParams.metrics = params.metrics;
  }

  if (params.sort_by) {
    apiParams.sort_by = params.sort_by;
  }

  if (params.channel) {
    apiParams.channel = params.channel;
  }

  if (params.filters && Object.keys(params.filters).length > 0) {
    apiParams.filters = params.filters;
  }

  try {
    const response = yield call(getProductAnalysis, apiParams);
    const result = response.data;

    yield put(
      actions.fetchSuccess({
        data: result.table_data || result.data || [],
        graphData: result.graph_data || null,
        meta: result.meta || null,
      })
    );
  } catch (error) {
    const errorMessage =
      error?.response?.data?.error ||
      error?.response?.data?.message ||
      error?.message ||
      'データの取得に失敗しました';

    yield put(actions.fetchError(errorMessage));

    // Dispatch to logger to trigger SystemErrorModal for server errors
    if (error?.response?.status >= 500) {
      yield put(
        loggerActions.logError(
          error.response?.data?.errors || [{ message: errorMessage }],
          loggerConstants.LOG_LEVEL_ERROR
        )
      );
    }
  }
}

function* handleDownloadCsv() {
  try {
    yield put(commonActions.setDownloadNotify('CSVファイルを作成しています...'));

    const params = yield select(selectParams);
    const period = yield select(
      (state) => state.commonState.CommonState.settings.period
    );
    const tab = yield select(settingsSelector.getTab);
    const channel = tab;
    const appliedFilters = yield select(filterSelectors.getApplicableSettings);
    const mappedFilters = mapAdebifyFilters(appliedFilters);

    const startDate = period?.start?.format('YYYY-MM-DD') || '';
    const endDate = period?.end?.format('YYYY-MM-DD') || '';

    const exportParams = {
      report_type: 'product_analysis',
      start_date: startDate,
      end_date: endDate,
      product_axis: params.product_axis,
      axis: params.axis,
      dimensions: dedupeDimensions(params.dimensions),
    };
    if (params.metrics) {
      exportParams.metrics = params.metrics;
    }
    if (channel) {
      exportParams.channel = channel;
    }
    if (Object.keys(mappedFilters).length > 0) {
      exportParams.filters = mappedFilters;
    }
    if (params.sort_by) {
      exportParams.sort_by = params.sort_by;
      exportParams.sort_order = params.sort_order;
    }

    const response = yield call(exportCsv, exportParams);

    // Response contains a presigned S3 download URL
    const downloadUrl = response.data?.download_url;
    if (downloadUrl) {
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.setAttribute('download', '');
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }

    yield put(commonActions.setDownloadNotify('CSVファイルを作成しました'));
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('CSV export failed:', error);
    yield put(commonActions.setDownloadNotify(''));
  }
}

export default function* adebifyProductAnalysisOperations() {
  yield takeLatest(types.FETCH_DATA, fetchProductAnalysisData);
  yield takeLatest(types.DOWNLOAD_CSV, handleDownloadCsv);
}

// Exported for saga payload contract tests.
export { fetchProductAnalysisData, handleDownloadCsv };
